import { useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { useSession } from "../features/auth/useSession";
import { signOut } from "../features/auth/session";
import { initials, avatarColor } from "../lib/avatar";

export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { user } = useSession();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const name = `${user.firstName} ${user.lastName}`.trim();

  const close = () => setAnchor(null);

  return (
    <Box sx={{ p: 1 }}>
      <ListItemButton
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={name}
        sx={{
          borderRadius: 2,
          minHeight: 44,
          justifyContent: compact ? "center" : "flex-start",
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 13,
              bgcolor: avatarColor(name),
            }}
          >
            {initials(user.firstName, user.lastName)}
          </Avatar>
        </ListItemIcon>
        {compact ? null : (
          <ListItemText
            primary={name}
            secondary={user.email}
            slotProps={{
              primary: { noWrap: true, variant: "body2", fontWeight: 600 },
              secondary: { noWrap: true, variant: "caption" },
            }}
          />
        )}
      </ListItemButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            close();
            void navigate("/settings/account");
          }}
        >
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t("nav.account")}
        </MenuItem>

        <Divider />
        <MenuItem
          onClick={() => {
            close();
            void signOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("nav.logout")}
        </MenuItem>
      </Menu>
    </Box>
  );
}
